// gate.ts — the capture loop's admission rules, kept pure so they are unit
// tested headless. Before a frame is posted to the WASM tracker the loop asks
// frameGate whether to track it: a frame whose size no longer matches the
// session (the phone was rotated, so the video's width and height swapped) is
// skipped with a "rotate back" hint instead of failing with ErrFrameSize, and
// tracking pauses while the keyframe upload backlog is at its cap, so a slow
// uplink cannot queue keyframes without bound (ARCHITECTURE.md §10).

// MAX_PENDING_UPLOADS caps keyframes encoded or uploading at once. Each holds
// one full-resolution JPEG Blob (~0.3–1 MB), never a raw bitmap.
export const MAX_PENDING_UPLOADS = 8;

export type HoldReason = 'rotate' | 'uploads';

export interface FrameHold {
  reason: HoldReason;
  message: string;
}

export interface FrameSize {
  width: number;
  height: number;
}

// orientationOf names a frame's orientation for the user.
export function orientationOf(s: FrameSize): 'portrait' | 'landscape' {
  return s.height > s.width ? 'portrait' : 'landscape';
}

// frameGate returns null when the frame may be tracked, or why it is held.
// The upload backlog is checked first: it applies even before a frame is
// grabbed, so the loop never draws (and allocates a bitmap for) a frame it
// will not track.
export function frameGate(frame: FrameSize | null, session: FrameSize | null, pendingUploads: number, cap = MAX_PENDING_UPLOADS): FrameHold | null {
  if (pendingUploads >= cap) {
    return { reason: 'uploads', message: `Waiting for ${pendingUploads} keyframe uploads — hold still` };
  }
  if (frame && session && (frame.width !== session.width || frame.height !== session.height)) {
    return { reason: 'rotate', message: `Rotate back to ${orientationOf(session)} to keep scanning` };
  }
  return null;
}
